// types
import { IField, IFieldValue } from 'types/graphql'
// utils
import { validate as isUuid } from 'uuid'
import { getTextFromHtml } from 'utils/html'

// cells are edited in a contentEditable, so their value is html with browser-inserted markup and nbsp
const getMatrixCellText = (value: string): string => getTextFromHtml(value).replace(/\u00a0/g, ' ').trim()

export const getFieldValueFullValue = (value: string, field: IField, fieldValue: IFieldValue) => {
  if (!value) {
    return null
  }

  const bRightSection: { id: string; value: string }[][] = field.settings?.bRightSection || []

  const selectedColumn = bRightSection.find((sectionArray) =>
    sectionArray.find((sectionValue) => sectionValue && sectionValue.id === value)
  )

  if (!selectedColumn) {
    return null
  }

  const columnOptionIndex = selectedColumn.findIndex((sectionValue) => sectionValue && sectionValue.id === value)

  const columnOption = selectedColumn[columnOptionIndex]

  if (!columnOption || columnOptionIndex === -1) {
    return null
  }

  const settingsOptions: { id: string; label: string; value: string }[] = field.settings?.options || []

  const valueOption = settingsOptions.find((option) => option.id === columnOption.value)

  const severityOption = selectedColumn.find((sectionValue) => sectionValue.value && !isUuid(sectionValue.value))

  const bLeftSection: { id: string; value: string }[][] = field.settings?.bLeftSection || []
  const bLeftSectionRow = bLeftSection[columnOptionIndex - 1]

  if (!bLeftSectionRow) {
    return null
  }

  const likelyOption = bLeftSectionRow[bLeftSectionRow.length - 1]

  if (!severityOption && !likelyOption && !valueOption) {
    return null
  }

  const topSection: { id: string; value: string }[][] = field.settings?.topSection || []

  const selectedTs = [] as { id: string; value: string }[]
  const selectedTsRaw = fieldValue?.value?.selectedTs as { id: string }[]
  const riskConsequenceCategories: { riskConsequenceCategory: string; consequence: string }[] = []

  if (selectedTsRaw?.length && topSection.length) {
    for (const selected of selectedTsRaw) {
      const section = topSection.find((arr) => arr?.some((v) => v?.id === selected?.id))

      const item = section?.find((v) => v?.id === selected?.id)

      if (item) {
        selectedTs.push(item)
      }
    }

    selectedTsRaw.forEach((sel) => {
      const row = topSection.find((r) => r.some((c) => c.id === sel.id))

      if (!row) {
        return
      }

      const rowLabel = String(row?.[0]?.value || '').toUpperCase()

      const colIndex = row.findIndex((c) => c.id === sel.id)

      if (colIndex === -1) {
        return
      }

      const bottomRow = bRightSection[colIndex - 1]
      const consequence = String(bottomRow?.[0]?.value || '').toUpperCase()

      if (rowLabel && consequence) {
        riskConsequenceCategories.push({ riskConsequenceCategory: rowLabel, consequence })
      }
    })
  }

  return {
    severityOption,
    likelyOption,
    valueOption,
    selectedTs,
    riskConsequenceCategories
  }
}

export const getMatrixSerializedValue = (value: string, field: IField, fieldValue: IFieldValue) => {
  const fullValue = getFieldValueFullValue(value, field, fieldValue)

  if (!fullValue) {
    return null
  }

  const { severityOption, likelyOption, valueOption, selectedTs, riskConsequenceCategories } = fullValue

  return {
    consequence: getMatrixCellText(severityOption?.value),
    likelihood: getMatrixCellText(likelyOption?.value),
    label: valueOption?.label,
    value: valueOption?.value,
    appetites: field.settings.appetites,
    appetiteEnabled: field.settings.appetiteEnabled,
    selectedTs: selectedTs.map((item) => item.value),
    riskConsequenceCategories
  }
}

// a matrix field value keeps the id of the selected bRightSection cell,
// so the matrix option itself has to be resolved through that cell
export const getMatrixSelectedOptionId = (value: any, field: IField): string | null => {
  const cellId = typeof value === 'string' ? value : value?.value

  if (!cellId || !field) {
    return null
  }

  const bRightSection: { id: string; value: string | { id: string } }[][] = field.settings?.bRightSection || []

  const cell = bRightSection.flat().find((sectionValue) => sectionValue && sectionValue.id === cellId)

  if (!cell) {
    return null
  }

  const optionId = typeof cell.value === 'string' ? cell.value : cell.value?.id
  const settingsOptions: { id: string }[] = field.settings?.options || []

  return settingsOptions.find((option) => option.id === optionId)?.id || null
}
